import { useFragment, useMutation } from '@base-app/web2'
import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CREATE_ADMIN_USER,
  CREATE_APP_USER,
  SET_USER_ADMIN,
  SET_USER_PASSWORD,
  UPDATE_APP_USER,
} from '../../../graphql/users/mutations'
import { USER_FORM_FRAGMENT } from '../../../graphql/users/queries'
import type { AppUser } from '../../../graphql/users/types'
import { useAuth } from '../../../hooks/useAuth'
import { usePermissionResources } from '../../../hooks/usePermissionResources'
import { useToast } from '../../../hooks/useToast'
import { accessLabelKey } from '../../../lib/accesses'
import { refetchQuietly } from '../../../lib/apolloRefetch'
import { prependToLists } from '../../../lib/cacheLists'
import {
  ACTIONS,
  type Action,
  isAction,
  type PermissionMap,
  satisfyRequirements,
  toggleAction,
} from '../../../lib/permissions'
import { Button, Checkbox, ErrorMessage, FormActions, Select, Skeleton } from '../../atoms'
import type { SelectOption } from '../../atoms/Select/Select'
import { FormField, FormLoadState, TextareaField } from '../../molecules'

interface UserFormFieldsProps {
  user?: AppUser
  applicationId?: string
  appId?: string
  applications?: { id: string; name: string; accesses: string[] }[]
  /** Superadmin-only: show the "global admin" toggle (grants the `admin` feature). */
  canManageGlobalAdmin?: boolean
  onSuccess: () => void
  onClose: () => void
}

// Same shape check as the API's assertEmail
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// The grid's column header of each action (also the second half of every checkbox's name)
const PERM_COLUMN_KEYS: Record<Action, string> = {
  get: 'users.form.permCol.view',
  post: 'users.form.permCol.create',
  patch: 'users.form.permCol.edit',
  delete: 'users.form.permCol.delete',
}

const parseUserFeatures = (user: AppUser | undefined, appId: string) => {
  if (!user || !appId) {
    const permissions: PermissionMap = {}
    return { isAppAdmin: false, permissions }
  }

  const isAppAdmin = user.features.includes(`app:${appId}:admin`)
  const map: PermissionMap = {}

  for (const f of user.features) {
    const match = f.match(new RegExp(`^app:${appId}:([^:]+):(.+)$`))
    if (match) {
      const [, resource, action] = match
      if (isAction(action)) {
        if (!map[resource]) {
          map[resource] = new Set()
        }
        map[resource].add(action)
      }
    }
  }

  return { isAppAdmin, permissions: map }
}

export const UserFormFields = memo(
  ({
    user,
    applicationId,
    appId,
    applications,
    canManageGlobalAdmin = false,
    onSuccess,
    onClose,
  }: UserFormFieldsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { user: authUser } = useAuth()
    const { showToast } = useToast()

    const isEdit = !!user
    // Who may set this user's password here (the API enforces the same rule): superadmins for
    // anyone, admins for users who aren't global admins
    const targetIsGlobalAdmin = !!(
      user?.features?.includes('admin') || user?.features?.includes('superadmin')
    )
    const canSetPassword =
      !isEdit ||
      !!authUser?.features.includes('superadmin') ||
      (!!authUser?.features.includes('admin') && !targetIsGlobalAdmin)
    const isGlobalAdmin = useMemo(
      () => !!(authUser?.features.includes('admin') || authUser?.features.includes('superadmin')),
      [authUser?.features],
    )

    // Superadmin accounts are never edited from here; the API rejects it too.
    const showGlobalAdminToggle =
      canManageGlobalAdmin && !(user?.features ?? []).includes('superadmin')
    const wasGlobalAdmin = (user?.features ?? []).includes('admin')
    const [isGlobalAdminUser, setIsGlobalAdminUser] = useState(wasGlobalAdmin)
    const managesGlobalAdmin = showGlobalAdminToggle && (isGlobalAdminUser || wasGlobalAdmin)

    const [selectedApplicationId, setSelectedApplicationId] = useState(applicationId ?? '')
    const effectiveApplicationId = applicationId ?? selectedApplicationId
    const effectiveApplication = useMemo(
      () => applications?.find(a => a.id === effectiveApplicationId) ?? null,
      [applications, effectiveApplicationId],
    )
    const effectiveAppId = appId ?? effectiveApplication?.id ?? ''
    const availableAccesses = effectiveApplication?.accesses ?? []
    const catalog = usePermissionResources()
    // The grid's rows: the catalog entries the app's accesses enable, in catalog order
    const permissionRows = useMemo(
      () => catalog.resources.filter(r => effectiveApplication?.accesses.includes(r.access)),
      [catalog.resources, effectiveApplication],
    )

    const [username, setUsername] = useState(user?.username ?? '')
    const [email, setEmail] = useState(user?.email ?? '')
    const [password, setPassword] = useState('')
    const [observations, setObservations] = useState(user?.observations ?? '')

    const initialFeatures = useMemo(
      () => parseUserFeatures(user, effectiveAppId),
      [user, effectiveAppId],
    )
    const [isAppAdmin, setIsAppAdmin] = useState(initialFeatures.isAppAdmin)
    const [permissions, setPermissions] = useState(initialFeatures.permissions)

    const handleApplicationChange = useCallback(
      (newApplicationId: string) => {
        setSelectedApplicationId(newApplicationId)
        const app = applications?.find(a => a.id === newApplicationId)
        const targetAppId = appId ?? app?.id ?? ''
        const { isAppAdmin: newAdmin, permissions: newPerms } = parseUserFeatures(user, targetAppId)
        setIsAppAdmin(newAdmin)
        setPermissions(newPerms)
      },
      [user, appId, applications],
    )

    const [errors, setErrors] = useState<{ username?: string; email?: string; password?: string }>(
      {},
    )

    // What the grid shows is exactly what gets saved: requirements are applied to whatever was
    // parsed from the user, also when the catalog arrives after it or the application changes
    const shownPermissions = useMemo(
      () => satisfyRequirements(permissions, catalog.resources),
      [permissions, catalog.resources],
    )

    const togglePermission = useCallback(
      (resource: string, action: Action) => {
        setPermissions(prev =>
          toggleAction(
            satisfyRequirements(prev, catalog.resources),
            catalog.resources,
            resource,
            action,
          ),
        )
      },
      [catalog.resources],
    )

    // The grid is the source of the features list. Without the catalog (still loading, or failed)
    // the list can't be built, and an empty one would erase the user's permissions on save (K-1).
    const gridApplies = !isGlobalAdminUser && !!effectiveAppId && availableAccesses.length > 0
    const catalogMissing = gridApplies && !isAppAdmin && (catalog.loading || !!catalog.error)
    // Edit: leave the stored permissions alone (the API keeps them when `features` is omitted).
    // Create: Save stays disabled until the catalog loads.
    const permissionsLocked = catalogMissing && !catalog.loading && isEdit
    const saveBlocked = catalogMissing && (catalog.loading || !isEdit)
    // An existing app admin can't be demoted without the catalog: omitting `features` would keep
    // app:X:admin, so the save would look successful and change nothing (F-R1). Promoting needs no catalog.
    const appAdminLocked =
      gridApplies && (catalog.loading || !!catalog.error) && isEdit && initialFeatures.isAppAdmin
    // The reason Save is disabled or the grid is read-only, linked to the button (F-U1)
    const saveNote = permissionsLocked
      ? t('users.form.permissionsLocked')
      : catalogMissing && !catalog.loading && !isEdit
        ? t('users.form.permissionsCreateBlocked')
        : null

    const features = useMemo((): string[] | undefined => {
      if (!effectiveAppId) {
        return []
      }
      if (isAppAdmin) {
        return [`app:${effectiveAppId}:admin`]
      }
      if (permissionsLocked) {
        return undefined
      }

      // Safety net: never send a set the API rejects (the grid already shows it completed)
      const complete = shownPermissions
      const feats: string[] = []
      for (const { key } of permissionRows) {
        for (const action of complete[key] ?? []) {
          feats.push(`app:${effectiveAppId}:${key}:${action}`)
        }
      }
      return feats
    }, [effectiveAppId, isAppAdmin, permissionsLocked, permissionRows, shownPermissions])

    const [createUser, { loading: creating }] = useMutation(CREATE_APP_USER, {
      update(cache, result, { variables }) {
        const item = result.data?.createAppUser
        if (!item) {
          return
        }
        prependToLists(cache, 'allUsers', item)
        prependToLists(cache, 'appUsers', item, { applicationId: variables?.applicationId })
      },
      onError: err => showToast(err.message, 'error'),
      onCompleted: () => onSuccess(),
    })

    const [updateUser, { loading: updating }] = useMutation(UPDATE_APP_USER, {
      onError: err => showToast(err.message, 'error'),
      onCompleted: () => onSuccess(),
    })

    const [createAdminUser, { loading: creatingAdmin }] = useMutation(CREATE_ADMIN_USER, {
      update(cache, result) {
        const item = result.data?.createAdminUser
        if (!item) {
          return
        }

        prependToLists(cache, 'allUsers', item)
      },
      onError: err => showToast(err.message, 'error'),
      onCompleted: () => onSuccess(),
    })

    const [setUserAdmin, { loading: settingAdmin }] = useMutation(SET_USER_ADMIN, {
      onError: err => showToast(err.message, 'error'),
    })

    const [setUserPassword, { loading: settingPassword }] = useMutation(SET_USER_PASSWORD, {
      onError: err => showToast(err.message, 'error'),
    })

    const loading = creating || updating || creatingAdmin || settingAdmin || settingPassword

    const validate = useCallback(() => {
      const errs: typeof errors = {}
      if (!username.trim()) {
        errs.username = t('users.form.errors.usernameRequired')
      }
      if (!isEdit && !email.trim()) {
        errs.email = t('users.form.errors.emailRequired')
      } else if (!isEdit && !EMAIL_RE.test(email.trim())) {
        errs.email = t('users.form.errors.emailInvalid')
      }
      if (!isEdit && !password.trim()) {
        errs.password = t('users.form.errors.passwordRequired')
      }
      if (password && password.length < 8) {
        errs.password = t('users.form.errors.passwordTooShort')
      }
      setErrors(errs)
      return Object.keys(errs).length === 0
    }, [username, email, password, isEdit, t])

    const handleSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) {
          return
        }

        // A new password typed on edit is saved first; the rest of the save waits for it
        let passwordSaved = false
        if (isEdit && password) {
          const result = await setUserPassword({
            variables: { id: user.id, password },
          }).catch(() => null)
          if (!result?.data?.setUserPassword) {
            return
          }
          passwordSaved = true
          showToast(t('users.form.passwordSaved'), 'success')
        }

        if (showGlobalAdminToggle && isGlobalAdminUser && !isEdit) {
          createAdminUser({
            variables: { username, email, password, observations: observations || null },
          })
          return
        }

        if (managesGlobalAdmin && isEdit) {
          const result = await setUserAdmin({
            variables: {
              id: user.id,
              isAdmin: isGlobalAdminUser,
              username: username || undefined,
              observations: observations || null,
            },
          }).catch(() => null)
          if (!result?.data) {
            return
          }
          // A demoted admin may also be given app access in the same save.
          if (isGlobalAdminUser || !effectiveApplicationId) {
            onSuccess()
            return
          }
        }

        if (!effectiveApplicationId) {
          // Editing someone without an app: a password change alone is a complete save
          if (passwordSaved) {
            onSuccess()
            return
          }
          showToast(t('users.form.errors.appRequired'), 'error')
          return
        }

        if (isEdit) {
          updateUser({
            variables: {
              id: user.id,
              applicationId: effectiveApplicationId,
              username: username || undefined,
              features,
              observations: observations || null,
            },
          })
          return
        }

        createUser({
          variables: {
            applicationId: effectiveApplicationId,
            username,
            email,
            password,
            features,
            observations: observations || null,
          },
        })
      },
      [
        validate,
        effectiveApplicationId,
        features,
        isEdit,
        user?.id,
        username,
        email,
        password,
        observations,
        updateUser,
        createUser,
        showGlobalAdminToggle,
        isGlobalAdminUser,
        managesGlobalAdmin,
        createAdminUser,
        setUserAdmin,
        setUserPassword,
        onSuccess,
        showToast,
        t,
      ],
    )

    const applicationOptions: SelectOption[] = useMemo(
      () => [
        { label: `— ${t('sidebar.selectApp')} —`, value: '', disabled: true },
        ...(applications ?? []).map(a => ({ label: a.name, value: a.id })),
      ],
      [applications, t],
    )

    return (
      <form onSubmit={handleSubmit} noValidate>
        <FormField
          id="user-username"
          label={t('users.form.usernameLabel')}
          name="username"
          value={username}
          onChange={e => setUsername(e.target.value)}
          error={errors.username}
          required
        />
        {!isEdit && (
          <FormField
            id="user-email"
            label={t('users.form.emailLabel')}
            name="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            error={errors.email}
            required
          />
        )}
        {canSetPassword && (
          <FormField
            id="user-password"
            label={t('users.form.passwordLabel')}
            name="password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            error={errors.password}
            required={!isEdit}
            hint={isEdit ? t('users.form.passwordEditHelper') : undefined}
          />
        )}
        <TextareaField
          id="user-observations"
          label={t('users.form.observationsLabel')}
          name="observations"
          value={observations}
          onChange={e => setObservations(e.target.value)}
        />

        {showGlobalAdminToggle && (
          <div className="form-group">
            <label htmlFor="user-is-global-admin" className="checkbox-label">
              <Checkbox
                id="user-is-global-admin"
                name="isGlobalAdmin"
                checked={isGlobalAdminUser}
                onChange={e => setIsGlobalAdminUser(e.target.checked)}
              />
              {t('users.form.isGlobalAdminLabel')}
            </label>
          </div>
        )}

        {!isGlobalAdminUser && !applicationId && applications && applications.length > 0 && (
          <div className="form-group">
            <label htmlFor="user-application">{t('sidebar.selectApp')}</label>
            <Select
              id="user-application"
              value={selectedApplicationId}
              onChange={handleApplicationChange}
              options={applicationOptions}
            />
          </div>
        )}

        {!isGlobalAdminUser && isGlobalAdmin && effectiveAppId ? (
          <div className="form-group">
            <label htmlFor="user-is-app-admin" className="checkbox-label">
              <Checkbox
                id="user-is-app-admin"
                name="isAppAdmin"
                checked={isAppAdmin}
                onChange={e => setIsAppAdmin(e.target.checked)}
                disabled={appAdminLocked}
              />
              {t('users.form.isAdminLabel')}
            </label>
            {appAdminLocked ? <p className="form-hint">{t('users.form.appAdminLocked')}</p> : null}
          </div>
        ) : null}

        {!isGlobalAdminUser && effectiveAppId && availableAccesses.length > 0 && (
          <div
            className={`form-group${isAppAdmin ? ' permission-matrix--disabled' : ''}`}
            aria-disabled={isAppAdmin}>
            <table className="permission-matrix">
              <caption className="permission-matrix__caption">
                {t('users.form.permissionsTitle')}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('applications.columns.accesses')}</th>
                  {ACTIONS.map(action => (
                    <th key={action} scope="col">
                      {t(PERM_COLUMN_KEYS[action])}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {catalog.loading
                  ? Array.from(availableAccesses, access => (
                      <tr key={access} aria-busy="true">
                        <th scope="row">
                          <Skeleton shape="text" />
                        </th>
                        {ACTIONS.map(action => (
                          <td key={action}>
                            <Checkbox skeleton />
                          </td>
                        ))}
                      </tr>
                    ))
                  : permissionRows.map(({ key }) => {
                      const rowLabel = t(accessLabelKey(key))
                      return (
                        <tr key={key}>
                          <th scope="row">{rowLabel}</th>
                          {ACTIONS.map(action => (
                            <td key={action}>
                              <Checkbox
                                id={`perm-${key}-${action}`}
                                name={`perm-${key}-${action}`}
                                aria-label={`${rowLabel} – ${t(PERM_COLUMN_KEYS[action])}`}
                                checked={shownPermissions[key]?.has(action) ?? false}
                                onChange={() => togglePermission(key, action)}
                                disabled={isAppAdmin}
                              />
                            </td>
                          ))}
                        </tr>
                      )
                    })}
              </tbody>
            </table>
            {catalog.error ? (
              <div role="alert">
                <ErrorMessage message={t('users.form.permissionsError')} />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => refetchQuietly(catalog.refetch())}>
                  {t('common.actions.retry')}
                </Button>
              </div>
            ) : null}
          </div>
        )}

        {saveNote ? (
          <p id="user-save-note" className="form-hint" role="note">
            {saveNote}
          </p>
        ) : null}

        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={loading || saveBlocked}
            aria-describedby={saveNote ? 'user-save-note' : undefined}>
            {loading ? t('users.form.saving') : t('users.form.save')}
          </Button>
        </FormActions>
      </form>
    )
  },
)

UserFormFields.displayName = 'UserFormFields'

interface UserFormProps extends Omit<UserFormFieldsProps, 'user'> {
  // The user to edit, by id; omit it to create a new one
  id?: string
}

// Reads the user by id from the cache the user lists filled
export const UserForm = memo(({ id, ...props }: UserFormProps) => {
  const { data, complete } = useFragment({
    fragment: USER_FORM_FRAGMENT,
    from: id ? { __typename: 'User', id } : null,
  })
  if (id && !complete) {
    return <FormLoadState status="missing" onClose={props.onClose} />
  }
  return <UserFormFields key={id ?? 'new'} user={id ? (data as AppUser) : undefined} {...props} />
})

UserForm.displayName = 'UserForm'
